import {
    addDays,
    addYears,
    addHours,
    startOfDay,
    endOfHour,
    isDateBetweenInclusive,
} from '@superbet-group/web.lib.utilities';

import OfferEvent from '@core/models/offer/OfferEvent';
import BookieDate from '@core/utils/date/BookieDate';
import { TimeFilter } from '@shared/timeFilter/types';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import dayTimeFilters from './dayTimeFilters';

export function getTimeFilterFromSlug(timeFilterSlug: string) {
    switch (timeFilterSlug) {
        case TimeFilterSlug.Today:
            return TimeFilter.Today;
        case TimeFilterSlug.Monday:
            return TimeFilter.Monday;
        case TimeFilterSlug.Tuesday:
            return TimeFilter.Tuesday;
        case TimeFilterSlug.Wednesday:
            return TimeFilter.Wednesday;
        case TimeFilterSlug.Thursday:
            return TimeFilter.Thursday;
        case TimeFilterSlug.Friday:
            return TimeFilter.Friday;
        case TimeFilterSlug.Saturday:
            return TimeFilter.Saturday;
        case TimeFilterSlug.Sunday:
            return TimeFilter.Sunday;
        case TimeFilterSlug.Live:
            return TimeFilter.Live;
        case TimeFilterSlug.Upcoming:
            return TimeFilter.Upcoming;
        case TimeFilterSlug.All:
            return TimeFilter.All;
        default:
            return shellLocalConfig.defaultSportTimeFilter;
    }
}

export function getDateFromDateSlug(date: string) {
    return new Date(date);
}

// opposite of getTimeFilterFromSlug
export function getSlugFromTimeFilter(timeFilter: TimeFilter) {
    return TimeFilterSlug[timeFilter];
}

export function getTimeFilterForWeekDay(dayIndex: number) {
    return dayTimeFilters[dayIndex];
}

export function isDate(timeFilter: TimeFilter) {
    return dayTimeFilters.includes(timeFilter);
}

export function getDayIndexForTimeFilter(timeFilter: TimeFilter) {
    return dayTimeFilters.indexOf(timeFilter);
}

export function getDateForSpecificWeekDay(weekDay: number) {
    const today = new Date();
    return addDays((weekDay - today.getDay() + 7) % 7, today);
}

export function getTimeFilterForDate(date: Date): TimeFilter {
    const targetDay = date.getDay();
    return getTimeFilterForWeekDay(targetDay);
}

// TODO(SW-4897): Either remove or extract into utilities library
function toStartOfMinute(date: Date): Date {
    const copiedDate = new Date(date);
    return new Date(copiedDate.setSeconds(0, 0));
}

export function getTimeSpanForTimeFilter(timeFilter: TimeFilter) {
    const today = new Date();
    // NOTE: We are using rounding to start of hour and end of hour in some cases:
    // - all
    // - live
    // - today
    // - upcoming
    // This is to improve cache hit ratio. It's an okay trade off to make since we are
    // getting a little bit more of the offer and it doesn't hurt the user experience.
    // TODO(sports-offer): update the handling of time filters to use custom offer endpoints like `/offer/upcoming`
    const todayStartOfMinute = toStartOfMinute(today);
    switch (timeFilter) {
        case TimeFilter.All:
            return {
                startDate: todayStartOfMinute,
                endDate: BookieDate.getEndDateForDate(addYears(1, todayStartOfMinute)),
            };
        case TimeFilter.Live:
        case TimeFilter.Today:
            return {
                startDate: todayStartOfMinute,
                endDate: BookieDate.getEndDateForDate(todayStartOfMinute),
            };
        case TimeFilter.Upcoming:
            const plusThreeHours = addHours(3, todayStartOfMinute);
            return {
                startDate: todayStartOfMinute,
                endDate: endOfHour(plusThreeHours),
            };
        default:
            break;
    }
    const dayIndex = getDayIndexForTimeFilter(timeFilter);
    const dayDifference = (dayIndex - today.getDay() + 7) % 7;
    const desiredDay = addDays(dayDifference, today);

    return {
        startDate: BookieDate.getStartDateForDate(desiredDay),
        endDate: BookieDate.getEndDateForDate(desiredDay),
    };
}

function getTimeSpanForDate(date: Date) {
    const startDate = startOfDay(date);
    return {
        startDate,
        endDate: addDays(1, startDate),
    };
}

export function getTimeFilterFunctionForTimeFilter(timeFilter: TimeFilter) {
    switch (timeFilter) {
        case TimeFilter.All:
            return Boolean;
        case TimeFilter.Live:
            return (event: OfferEvent) => {
                if (event instanceof OfferEvent) {
                    return event.isOngoing();
                }
                return false;
            };
        default:
            const {
                startDate,
                endDate,
            } = getTimeSpanForTimeFilter(timeFilter);
            return (event: OfferEvent) => {
                if (event instanceof OfferEvent && event.isOngoing()) {
                    return false;
                }

                return isDateBetweenInclusive(startDate, endDate, event.date);
            };
    }
}

export function getTimeFilterFunctionForDate(date: Date) {
    const {
        startDate,
        endDate,
    } = getTimeSpanForDate(date);
    return (event: OfferEvent) => {
        return isDateBetweenInclusive(startDate, endDate, event.date);
    };
}
